import { Text } from '@blocksuite/affine/store';

import type { DocsService } from '../doc';

export type OrgTemplateKey = 'budget' | 'timeline' | 'team';

interface ColumnSpec {
  name: string;
  type: string;
  data?: Record<string, unknown>;
}

interface TemplateSpec {
  key: OrgTemplateKey;
  title: string;
  columns: ColumnSpec[];
  /**
   * Seeded rows so the table and «Инфокарта» are not empty on first open.
   * Dates are applied through the data-view API after row blocks exist — the
   * raw `cells` prop does not survive for date-range columns.
   */
  rows?: { title?: string; dateRange?: [number, number] }[];
}

/**
 * Ready-made table structures for the three documents the meeting kept
 * mentioning: смета, тайминг, команда. They are ordinary docs with a database
 * block, so the organizer can copy them or mark them as a template.
 */
export const ORG_TEMPLATES: Record<OrgTemplateKey, TemplateSpec> = {
  budget: {
    key: 'budget',
    title: 'Смета',
    rows: [{ title: 'Площадка' }, { title: 'Кейтеринг' }, { title: 'Декор' }],
    columns: [
      { name: 'Позиция', type: 'title' },
      { name: 'Категория', type: 'text' },
      {
        name: 'План',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
      {
        name: 'Факт',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
      {
        name: 'Оплачено',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
    ],
  },
  timeline: {
    key: 'timeline',
    title: 'Тайминг',
    rows: [
      { title: 'Подготовка', dateRange: [-7, 7] },
      { title: 'Согласование сметы', dateRange: [0, 10] },
      { title: 'Утверждение площадки', dateRange: [2, 6] },
      { title: 'Депозит площадки', dateRange: [6, 7] },
      { title: 'Выбор фотографа', dateRange: [3, 9] },
      { title: 'Договор с фотографом', dateRange: [9, 10] },
      { title: 'Лав-стори', dateRange: [12, 12] },
      { title: 'Выбор видеографа', dateRange: [4, 11] },
      { title: 'Договор с видеографом', dateRange: [11, 12] },
      { title: 'Дегустация меню', dateRange: [10, 14] },
      { title: 'Утверждение меню', dateRange: [14, 15] },
      { title: 'Рассылка приглашений', dateRange: [8, 20] },
      { title: 'Сбор ответов гостей', dateRange: [20, 30] },
      { title: 'Флористика: концепция', dateRange: [12, 18] },
      { title: 'Заказ цветов', dateRange: [25, 28] },
      { title: 'Дегустация торта', dateRange: [16, 18] },
      { title: 'Заказ торта', dateRange: [18, 19] },
      { title: 'Диджей: выбор', dateRange: [15, 22] },
      { title: 'Договор с диджеем', dateRange: [22, 23] },
      { title: 'Аренда платья', dateRange: [14, 21] },
      { title: 'Примерка платья', dateRange: [24, 24] },
      { title: 'Костюм жениха', dateRange: [16, 23] },
      { title: 'Покупка колец', dateRange: [18, 25] },
      { title: 'Визажист: репетиция', dateRange: [26, 26] },
      { title: 'Трансфер гостей', dateRange: [30, 32] },
      { title: 'Сценарий вечера', dateRange: [28, 34] },
      { title: 'Репетиция церемонии', dateRange: [34, 34] },
      { title: 'Финальная сверка сметы', dateRange: [35, 36] },
      { title: 'День свадьбы', dateRange: [36, 38] },
      { title: 'Отъезд / медовый месяц', dateRange: [39, 45] },
    ],
    columns: [
      { name: 'Этап', type: 'title' },
      // a date-range column carries both ends, so «Инфокарта» draws one bar per row
      { name: 'Срок', type: 'date-range' },
      { name: 'Ответственный', type: 'text' },
      { name: 'Комментарий', type: 'text' },
    ],
  },
  team: {
    key: 'team',
    title: 'Команда',
    rows: [{ title: 'Ведущий' }, { title: 'Фотограф' }],
    columns: [
      { name: 'Имя', type: 'title' },
      { name: 'Роль', type: 'text' },
      { name: 'Контакт', type: 'text' },
      {
        name: 'Ставка',
        type: 'number',
        data: { decimal: 0, format: 'currencyRUB' },
      },
    ],
  },
};

const newId = () => crypto.randomUUID();

export async function createTemplateDoc(
  docsService: DocsService,
  key: OrgTemplateKey,
  title?: string
): Promise<string> {
  const spec = ORG_TEMPLATES[key];
  const docRecord = docsService.createDoc({ title: title ?? spec.title });

  const { doc, release } = docsService.open(docRecord.id);
  try {
    await doc.waitForSyncReady();
    const store = doc.blockSuiteDoc;
    const note = store.getBlocksByFlavour('affine:note').at(0);
    if (!note) return docRecord.id;

    const pendingCells: Record<
      string,
      Record<string, { columnId: string; value: unknown }>
    > = {};
    let pendingDbId = '';

    store.transact(() => {
      const columns = spec.columns.map(column => ({
        id: newId(),
        type: column.type,
        name: column.name,
        data: column.data ?? {},
      }));
      const titleColumnId = columns.find(c => c.type === 'title')?.id;
      // date-range column → the gantt view can draw a bar per row
      const dateColumnId = columns.find(c => c.type === 'date-range')?.id;

      store.addBlock(
        'affine:paragraph',
        { type: 'h2', text: new Text(title ?? spec.title) },
        note.id
      );
      const dbId = store.addBlock(
        'affine:database',
        {
          title: new Text(title ?? spec.title),
          views: [
            ...(dateColumnId && spec.key === 'timeline'
              ? [
                  {
                    id: newId(),
                    name: 'Инфокарта',
                    mode: 'gantt',
                    filter: { type: 'group', op: 'and', conditions: [] },
                    date: { startColumnId: dateColumnId },
                    header: { titleColumn: titleColumnId },
                    // a two-month wedding timeline reads best by weeks
                    scale: 'week',
                  },
                ]
              : []),
            {
              id: newId(),
              name: 'Таблица',
              mode: 'table',
              columns: columns.map(column => ({
                id: column.id,
                width: 200,
              })),
              filter: { type: 'group', op: 'and', conditions: [] },
              header: { titleColumn: titleColumnId, iconColumn: 'type' },
            },
            ...(dateColumnId && spec.key !== 'timeline'
              ? [
                  {
                    id: newId(),
                    name: 'Инфокарта',
                    mode: 'gantt',
                    filter: { type: 'group', op: 'and', conditions: [] },
                    date: { startColumnId: dateColumnId },
                    header: { titleColumn: titleColumnId },
                    scale: 'week',
                  },
                ]
              : []),
          ],
          columns,
          cells: {},
        },
        note.id
      );

      // Rows must exist before the cells map can reference them: `addBlock`
      // returns the real row id, a pre-generated one leaves the row empty.
      const DAY_MS = 24 * 60 * 60 * 1000;
      const base = Date.now();
      (spec.rows ?? []).forEach((row, index) => {
        const title = row.title ?? '';
        const rowId = store.addBlock(
          'affine:paragraph',
          { text: new Text(title) },
          dbId
        );
        const rowCells: Record<
          string,
          { columnId: string; value: unknown }
        > = {};
        if (title && titleColumnId) {
          rowCells[titleColumnId] = { columnId: titleColumnId, value: title };
        }
        if (row.dateRange && dateColumnId) {
          rowCells[dateColumnId] = {
            columnId: dateColumnId,
            value: {
              start: base + row.dateRange[0] * DAY_MS,
              end: base + row.dateRange[1] * DAY_MS,
            },
          };
        }
        if (Object.keys(rowCells).length) pendingCells[rowId] = rowCells;
        void index;
      });

      pendingDbId = dbId;

      store.addBlock(
        'affine:paragraph',
        {
          text: new Text(
            'Заполните таблицу — колонки уже настроены. Документ можно отметить как шаблон в свойствах страницы.'
          ),
        },
        note.id
      );
    });

    // Cells are written after the transaction commits: rows and their ids only
    // exist as committed blocks, and writing `cells` inside the same transact
    // left every date cell empty.
    if (pendingDbId && Object.keys(pendingCells).length) {
      store.updateBlock(pendingDbId, { cells: pendingCells });
    }
  } finally {
    release();
  }

  return docRecord.id;
}
